import { handleUnauthorized, readApiErrorMessage } from '@utils/apiHelpers'
import { t } from '@store/I18nStore'
import { timeShort } from '@utils/dateTime'

export const RESEND_VERIFICATION_PATH = '/api/auth/resend-verification'
export const VERIFY_EMAIL_PATH = '/api/auth/verify-email'
export const EMAIL_VERIFICATION_RESEND_RATE_LIMIT_CODE = 'EMAIL_VERIFICATION_RESEND_RATE_LIMIT'
export const RESEND_COOLDOWN_MS = 60_000
export const RESEND_COOLDOWN_SECONDS = 60

/** "42 с" / "57 мин 39 с" / "1 ч 2 мин" — hour-scale waits after 4 resends per hour. */
export function formatResendWaitLabel(seconds: number): string {
  const totalSec = Math.max(1, Math.ceil(seconds))
  const hours = Math.floor(totalSec / 3600)
  const minutes = Math.floor((totalSec % 3600) / 60)
  const sec = totalSec % 60
  const hour = timeShort('hour')
  const second = timeShort('second')
  const min = timeShort('min')
  if (hours > 0) return minutes > 0 ? `${hours} ${hour} ${minutes} ${min}` : `${hours} ${hour}`
  if (minutes > 0) return sec > 0 ? `${minutes} ${min} ${sec} ${second}` : `${minutes} ${min}`
  return `${sec} ${second}`
}

function trimBase(apiBaseUrl: string): string {
  return apiBaseUrl.replace(/\/$/, '')
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function pickMessage(data: unknown, fallback: string): string {
  const record = asRecord(data)
  if (!record) return fallback
  const message = typeof record.message === 'string' ? record.message.trim() : ''
  if (message) return message
  const error = typeof record.error === 'string' ? record.error.trim() : ''
  return error || fallback
}

function readRetryAfterSeconds(data: unknown, response: Response): number {
  const root = asRecord(data)
  const details = asRecord(root?.details)
  const fromDetails = Number(details?.retryAfterSeconds)
  if (Number.isFinite(fromDetails) && fromDetails > 0) return Math.ceil(fromDetails)

  const header = response.headers.get('Retry-After')
  const fromHeader = header ? Number(header) : NaN
  if (Number.isFinite(fromHeader) && fromHeader > 0) return Math.ceil(fromHeader)

  return RESEND_COOLDOWN_SECONDS
}

export class EmailVerificationResendRateLimitError extends Error {
  readonly code = EMAIL_VERIFICATION_RESEND_RATE_LIMIT_CODE
  readonly retryAfterSeconds: number

  constructor(retryAfterSeconds: number, message?: string) {
    const seconds = Math.max(1, Math.ceil(retryAfterSeconds))
    super(message?.trim() || t('auth.resend.available_in', { seconds: formatResendWaitLabel(seconds) }).replace(` ${t('time.short.sec')}.`, ''))
    this.name = 'EmailVerificationResendRateLimitError'
    this.retryAfterSeconds = seconds
  }
}

/** Sends another confirmation letter. Authenticated — Authorization only, no body. */
export async function resendEmailVerification(
  apiBaseUrl: string,
  headers: Record<string, string>,
): Promise<string> {
  const base = trimBase(apiBaseUrl)
  const authorization = headers.Authorization || headers.authorization
  if (!authorization) {
    throw new Error(t('errors.auth.no_session'))
  }

  const response = await fetch(`${base}${RESEND_VERIFICATION_PATH}`, {
    method: 'POST',
    headers: { Authorization: authorization },
  })

  if (handleUnauthorized(response)) {
    throw new Error(t('auth.session.expired'))
  }

  if (response.status === 429) {
    const data = await response.json().catch(() => null)
    throw new EmailVerificationResendRateLimitError(
      readRetryAfterSeconds(data, response),
      pickMessage(data, ''),
    )
  }

  if (response.ok) {
    const data = await response.json().catch(() => null)
    return pickMessage(data, t('auth.success.confirm_email_sent'))
  }

  throw new Error(await readApiErrorMessage(response, t('errors.auth.send_email')))
}

export type EmailVerificationResult = {
  message: string
  emailChanged: boolean
  requiresLogin: boolean
}

/** Confirms email with the token from the letter. Public — no session required. */
export async function verifyEmailByToken(apiBaseUrl: string, token: string): Promise<EmailVerificationResult> {
  const base = trimBase(apiBaseUrl)
  const response = await fetch(`${base}${VERIFY_EMAIL_PATH}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  })

  const data = await response.json().catch(() => null)
  const successFlag =
    data && typeof data === 'object' ? (data as { success?: unknown }).success : undefined

  if (response.ok && successFlag !== false) {
    const result = asRecord(asRecord(data)?.data)
    const emailChanged = result?.emailChanged === true
    return {
      message: pickMessage(data, t('auth.email_confirm.success')),
      emailChanged,
      requiresLogin: result?.requiresLogin === true || emailChanged,
    }
  }

  throw new Error(pickMessage(data, t('errors.auth.token_invalid')))
}
